import {
  type Dispatch,
  type MutableRefObject,
  useCallback,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { z, ZodError, type ZodType } from 'zod';

import { formatFileSize } from '@vestigas/utils';

import {
  type DataImportConfigInput,
  type DataImportConfirmPolicy,
  type DataImportFileValidator,
  type FormState,
  resolveDataImportConfig,
  type ResolvedDataImportFile,
} from './dataImport.config';
import { type DataImportCopy, useDataImportCopy } from './dataImport.copy';
import {
  getReadableSupportedCsvDelimiters,
  reverseColumnMappingForApi,
} from './dataImport.helpers';
import { csvDelimitersOf } from './dataImport.parsers';
import {
  parseImportFile,
  type ParsedImportFile,
  type ParseImportFileOptions,
  validateImportRows,
} from './dataImport.parsing';
import {
  buildDataImportPreviewModel,
  checkMaxRowsExceeded,
  mergeDataImportPreviewIssues,
  normalizeDataImportPreviewIssues,
  type DataImportPreviewLocalIssue,
  type DataImportPreviewRemoteIssue,
} from './dataImport.preview';
import {
  canConfirmDataImport,
  createDataImportWizardReducer,
  createDataImportWizardState,
  type DataImportWizardAction,
  type DataImportWizardState,
  type ReadyState,
} from './dataImportWizard.state';
import {
  type DataImportWizardSteps,
  hasDataImportMappingStep,
  resolveDataImportWizardSteps,
} from './dataImportWizard.steps';

type DataImportWizardRequestInput = {
  columnMapping?: Record<string, string>;
  file: File;
  formData?: Record<string, unknown>;
};

export type DataImportWizardInput<TParsedData = unknown> =
  DataImportWizardRequestInput & {
    parsedData: TParsedData;
  };

export type DataImportWizardValidationInput<TParsedData = unknown> =
  DataImportWizardRequestInput & {
    parsedData?: TParsedData;
  };

function createRequestInput<TPreview, TResult, TParsedData>(
  state: DataImportWizardState<TPreview, TResult, TParsedData>,
  hasAdditionalForm: boolean,
): DataImportWizardRequestInput | undefined {
  if (!state.file) return undefined;

  return {
    columnMapping: reverseColumnMappingForApi(state.columnMapping),
    file: state.file,
    formData: hasAdditionalForm ? state.formState.data : undefined,
  };
}

function createInput<TPreview, TResult, TParsedData>(
  state: DataImportWizardState<TPreview, TResult, TParsedData>,
  hasAdditionalForm: boolean,
  parsedData: TParsedData,
): DataImportWizardInput<TParsedData> | undefined {
  const requestInput = createRequestInput(state, hasAdditionalForm);
  if (!requestInput) return undefined;

  return {
    ...requestInput,
    parsedData,
  };
}

type WizardDispatch<TPreview, TResult, TParsedData> = Dispatch<
  DataImportWizardAction<TPreview, TResult, TParsedData>
>;

function asError(error: unknown) {
  return error instanceof Error ? error : new Error(String(error));
}

type LocalValidationResult<TParsedData> =
  | { status: 'failed'; error: unknown }
  | { status: 'succeeded'; parsedData: TParsedData };

function validatePreviewRows<TValidationSchema extends ZodType>(
  validationRules: TValidationSchema,
  state: DataImportWizardState<unknown, unknown, z.output<TValidationSchema>>,
): LocalValidationResult<z.output<TValidationSchema>> {
  try {
    return {
      status: 'succeeded',
      parsedData: validateImportRows({
        columnMapping: state.columnMapping,
        fileContent: state.parsedFile?.fileContent ?? [],
        schema: validationRules,
      }),
    };
  } catch (error) {
    return { status: 'failed', error };
  }
}

type LocalPreviewOutcome<TParsedData> =
  | { status: 'unusable'; error: Error }
  | {
      status: 'usable';
      issues: ReturnType<typeof normalizeDataImportPreviewIssues>;
      parsedData: ReadyState<TParsedData>;
    };

/**
 * Separates the two ways local validation can end. Schema violations still
 * yield a usable preview — they are surfaced as per-row issues so the user can
 * correct the file — whereas any other failure leaves nothing to preview.
 */
function derivePreviewValidationOutcome<TParsedData>(
  localValidation: LocalValidationResult<TParsedData>,
): LocalPreviewOutcome<TParsedData> {
  if (localValidation.status === 'succeeded') {
    return {
      status: 'usable',
      issues: [],
      parsedData: { status: 'ready', value: localValidation.parsedData },
    };
  }

  if (localValidation.error instanceof ZodError) {
    return {
      status: 'usable',
      issues: normalizeDataImportPreviewIssues(localValidation.error),
      parsedData: { status: 'idle' },
    };
  }

  return { status: 'unusable', error: asError(localValidation.error) };
}

async function validateRemotePreview<TPreview, TResult, TParsedData>({
  dispatch,
  getRemoteIssues,
  localIssues,
  onValidate,
  parsedData,
  previewModel,
  previewRequest,
  request,
  requestInput,
}: {
  dispatch: WizardDispatch<TPreview, TResult, TParsedData>;
  getRemoteIssues?: (
    previewResult: TPreview,
  ) =>
    | Promise<readonly DataImportPreviewRemoteIssue[]>
    | readonly DataImportPreviewRemoteIssue[];
  localIssues: ReturnType<typeof normalizeDataImportPreviewIssues>;
  onValidate: (
    input: DataImportWizardValidationInput<TParsedData>,
  ) => Promise<TPreview> | TPreview;
  parsedData: ReadyState<TParsedData>;
  previewModel: ReturnType<typeof buildDataImportPreviewModel>;
  previewRequest: MutableRefObject<number>;
  request: number;
  requestInput: DataImportWizardRequestInput;
}) {
  try {
    const preview = await onValidate({
      ...requestInput,
      parsedData: parsedData.status === 'ready' ? parsedData.value : undefined,
    });
    const remoteIssues = await getRemoteIssues?.(preview);
    if (request !== previewRequest.current) return;
    dispatch({
      parsedData,
      preview,
      previewModel: {
        ...previewModel,
        issues: mergeDataImportPreviewIssues(localIssues, remoteIssues),
      },
      type: 'previewSucceeded',
    });
  } catch (error) {
    if (request !== previewRequest.current) return;
    dispatch({ type: 'previewFailed', error: asError(error) });
  }
}

function useFileSelection<TPreview, TResult, TParsedData>(
  dispatch: WizardDispatch<TPreview, TResult, TParsedData>,
  parseOptions: ParseImportFileOptions,
) {
  const parseRequest = useRef(0);

  const selectFile = useCallback(
    async (file: File) => {
      const request = ++parseRequest.current;
      dispatch({ type: 'fileSelected', file });

      try {
        const parsedFile = await parseImportFile(file, parseOptions);

        if (request === parseRequest.current) {
          dispatch({ type: 'fileParsed', parsedFile });
        }
      } catch (error) {
        if (request === parseRequest.current) {
          dispatch({ type: 'fileParseFailed', error: asError(error) });
        }
      }
    },
    [dispatch, parseOptions],
  );
  const removeFile = useCallback(() => {
    parseRequest.current += 1;
    dispatch({ type: 'fileRemoved' });
  }, [dispatch]);

  return { removeFile, selectFile };
}

/**
 * Runs the whole-file checks a row schema cannot express: a custom validator,
 * if configured, plus the row-count cap. Returns them in the order the
 * preview lists local issues after row-schema ones, so a caller only has to
 * append the result.
 */
async function runFileLevelValidation({
  columnMapping,
  copy,
  file,
  maxRows,
  parsedFile,
  validateFile,
}: {
  columnMapping?: Record<string, string>;
  copy: Pick<DataImportCopy, 'maxRowsExceeded'>;
  file: File;
  maxRows?: number;
  parsedFile: ParsedImportFile;
  validateFile?: DataImportFileValidator;
}): Promise<DataImportPreviewLocalIssue[]> {
  const validatorIssues = validateFile
    ? [...(await validateFile({ columnMapping, file, parsedFile }))]
    : [];
  const rowCount = parsedFile.fileContent.length;
  const maxRowsIssues = checkMaxRowsExceeded({
    maxRows,
    message:
      maxRows === undefined ? '' : copy.maxRowsExceeded(rowCount, maxRows),
    rowCount,
  });

  return [...validatorIssues, ...maxRowsIssues];
}

function usePreviewValidation<
  TPreview,
  TResult,
  TValidationSchema extends ZodType,
>({
  copy,
  dispatch,
  fileConfig,
  getRemoteIssues,
  hasAdditionalForm,
  locale,
  onValidate,
  previewRequest,
  state,
  validateFile,
}: {
  copy: Pick<DataImportCopy, 'maxRowsExceeded'>;
  dispatch: WizardDispatch<TPreview, TResult, z.output<TValidationSchema>>;
  fileConfig: ResolvedDataImportFile<TValidationSchema>;
  getRemoteIssues?: (
    previewResult: TPreview,
  ) =>
    | Promise<readonly DataImportPreviewRemoteIssue[]>
    | readonly DataImportPreviewRemoteIssue[];
  hasAdditionalForm: boolean;
  locale: string;
  onValidate: (
    input: DataImportWizardValidationInput<z.output<TValidationSchema>>,
  ) => Promise<TPreview> | TPreview;
  previewRequest: MutableRefObject<number>;
  state: DataImportWizardState<TPreview, TResult, z.output<TValidationSchema>>;
  validateFile?: DataImportFileValidator;
}) {
  return useCallback(async () => {
    if (!state.parsedFile) return;
    const request = ++previewRequest.current;
    const previewModel = buildDataImportPreviewModel({
      allHeaderFields: fileConfig.allHeaderFields,
      columnMapping: state.columnMapping,
      dateFields: fileConfig.dateFields,
      fileContent: state.parsedFile.previewFileContent,
      locale,
      rawFileContent: state.parsedFile.fileContent,
    });

    const localValidation = validatePreviewRows(
      fileConfig.validationRules,
      state,
    );
    const outcome = derivePreviewValidationOutcome(localValidation);
    if (outcome.status === 'unusable') {
      if (request !== previewRequest.current) return;
      dispatch({ type: 'previewStarted', previewModel });
      dispatch({ type: 'previewFailed', error: outcome.error });
      return;
    }

    const requestInput = createRequestInput(state, hasAdditionalForm);
    if (!requestInput) return;

    // Dispatched before the file-level checks below so the step transitions
    // and the row-schema issues stay visible even if a validator throws.
    const locallyValidatedPreviewModel = {
      ...previewModel,
      issues: outcome.issues,
    };
    dispatch({
      type: 'previewStarted',
      previewModel: locallyValidatedPreviewModel,
    });

    let fileLevelIssues: DataImportPreviewLocalIssue[];
    try {
      fileLevelIssues = await runFileLevelValidation({
        columnMapping: state.columnMapping,
        copy,
        file: requestInput.file,
        maxRows: fileConfig.maxRows,
        parsedFile: state.parsedFile,
        validateFile,
      });
    } catch (error) {
      if (request !== previewRequest.current) return;
      dispatch({ type: 'previewFailed', error: asError(error) });
      return;
    }
    if (request !== previewRequest.current) return;

    const localIssues = [...outcome.issues, ...fileLevelIssues];

    await validateRemotePreview({
      dispatch,
      getRemoteIssues,
      localIssues,
      onValidate,
      parsedData: outcome.parsedData,
      previewModel: locallyValidatedPreviewModel,
      previewRequest,
      request,
      requestInput,
    });
  }, [
    copy,
    dispatch,
    fileConfig.allHeaderFields,
    fileConfig.dateFields,
    fileConfig.maxRows,
    fileConfig.validationRules,
    getRemoteIssues,
    hasAdditionalForm,
    locale,
    onValidate,
    previewRequest,
    state,
    validateFile,
  ]);
}

function useImportConfirmation<TPreview, TResult, TParsedData>({
  canConfirm,
  dispatch,
  hasAdditionalForm,
  onImport,
  state,
}: {
  canConfirm?: DataImportConfirmPolicy<TPreview>;
  dispatch: WizardDispatch<TPreview, TResult, TParsedData>;
  hasAdditionalForm: boolean;
  onImport: (
    input: DataImportWizardInput<TParsedData>,
  ) => Promise<TResult> | TResult;
  state: DataImportWizardState<TPreview, TResult, TParsedData>;
}) {
  return useCallback(async () => {
    // The same gate the confirm button renders from, so a keyboard or
    // programmatic call cannot import what the UI refuses to.
    if (!canConfirmDataImport(state, canConfirm)) return;
    const input = createInput(state, hasAdditionalForm, state.parsedData.value);
    if (!input) return;

    dispatch({ type: 'importStarted' });

    try {
      const result = await onImport(input);
      dispatch({ type: 'importSucceeded', result });
    } catch (error) {
      dispatch({ type: 'importFailed', error: asError(error) });
    }
  }, [canConfirm, dispatch, hasAdditionalForm, onImport, state]);
}

/**
 * The wizard's user-facing actions. Every action that can invalidate an
 * in-flight preview bumps `previewRequest` first, so a stale validation
 * result is recognized and dropped when it settles.
 */
function useDataImportWizardActions<TPreview, TResult, TParsedData>({
  confirmImport,
  dispatch,
  preparePreview,
  previewRequest,
  removeFile,
  selectFile,
}: {
  confirmImport: () => Promise<void>;
  dispatch: WizardDispatch<TPreview, TResult, TParsedData>;
  preparePreview: () => Promise<void>;
  previewRequest: MutableRefObject<number>;
  removeFile: () => void;
  selectFile: (file: File) => Promise<void>;
}) {
  const updateFormState = useCallback(
    (formState: FormState) => {
      previewRequest.current += 1;
      dispatch({ type: 'formUpdated', formState });
    },
    [dispatch, previewRequest],
  );
  const updateColumnMapping = useCallback(
    ({
      columnMapping,
      isValid,
    }: {
      columnMapping: Record<string, string>;
      isValid: boolean;
    }) => {
      previewRequest.current += 1;
      dispatch({ type: 'mappingUpdated', columnMapping, isValid });
    },
    [dispatch, previewRequest],
  );
  const goBack = useCallback(() => dispatch({ type: 'back' }), [dispatch]);
  const goToMapping = useCallback(
    () => dispatch({ type: 'setStep', step: 'mapping' }),
    [dispatch],
  );
  const selectFileForImport = useCallback(
    (file: File) => {
      previewRequest.current += 1;
      void selectFile(file);
    },
    [previewRequest, selectFile],
  );
  const removeFileForImport = useCallback(() => {
    previewRequest.current += 1;
    removeFile();
  }, [previewRequest, removeFile]);
  const validatePreview = useCallback(
    () => void preparePreview(),
    [preparePreview],
  );
  const importFile = useCallback(() => void confirmImport(), [confirmImport]);

  return {
    goBack,
    goToMapping,
    importFile,
    removeFile: removeFileForImport,
    selectFileForImport,
    updateColumnMapping,
    updateFormState,
    validatePreview,
  };
}

export function useDataImportWizard<
  TPreview,
  TResult,
  TValidationSchema extends ZodType = ZodType,
>({
  config,
  getRemoteIssues,
  onImport,
  onValidate,
  steps,
}: {
  config: DataImportConfigInput<TValidationSchema, TPreview>;
  getRemoteIssues?: (
    previewResult: TPreview,
  ) =>
    | Promise<readonly DataImportPreviewRemoteIssue[]>
    | readonly DataImportPreviewRemoteIssue[];
  onImport: (
    input: DataImportWizardInput<z.output<TValidationSchema>>,
  ) => Promise<TResult> | TResult;
  onValidate: (
    input: DataImportWizardValidationInput<z.output<TValidationSchema>>,
  ) => Promise<TPreview> | TPreview;
  /** Defaults to file → mapping → preview. */
  steps?: DataImportWizardSteps;
}) {
  const { i18n } = useTranslation('ui');
  const wizardSteps = useMemo(
    () => resolveDataImportWizardSteps(steps),
    [steps],
  );
  const hasMappingStep = useMemo(
    () => hasDataImportMappingStep(wizardSteps),
    [wizardSteps],
  );
  const reducer = useMemo(
    () =>
      createDataImportWizardReducer<
        TPreview,
        TResult,
        z.output<TValidationSchema>
      >(wizardSteps),
    [wizardSteps],
  );
  const [state, dispatch] = useReducer(
    reducer,
    createDataImportWizardState<
      TPreview,
      TResult,
      z.output<TValidationSchema>
    >(),
  );
  const previewRequest = useRef(0);
  const resolvedConfig = useMemo(
    () => resolveDataImportConfig(config),
    [config],
  );
  const hasAdditionalForm = Boolean(resolvedConfig.renderAdditionalForm);
  const copy = useDataImportCopy(resolvedConfig.copy);
  const numberLocale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const parseOptions = useMemo(
    () => ({
      acceptedFileTypes: resolvedConfig.file.acceptedFileTypes,
      csvParseFailedMessage: copy.csvParseFailed,
      emptyFileMessage: copy.emptyFile,
      fileTooLargeMessage: copy.fileTooLarge(
        formatFileSize(resolvedConfig.file.maxFileSize, numberLocale),
      ),
      inconsistentFieldCountMessage: copy.inconsistentFieldCount,
      maxFileSize: resolvedConfig.file.maxFileSize,
      parsers: resolvedConfig.file.parsers,
      unsupportedDelimiterMessage: copy.unsupportedDelimiter(
        getReadableSupportedCsvDelimiters(
          csvDelimitersOf(resolvedConfig.file.parsers),
        ),
      ),
      unsupportedFileTypeMessage: copy.invalidFileType(
        resolvedConfig.file.acceptedFileTypes,
      ),
    }),
    [
      copy,
      numberLocale,
      resolvedConfig.file.acceptedFileTypes,
      resolvedConfig.file.maxFileSize,
      resolvedConfig.file.parsers,
    ],
  );
  const { removeFile, selectFile } = useFileSelection(dispatch, parseOptions);
  const preparePreview = usePreviewValidation({
    copy,
    dispatch,
    fileConfig: resolvedConfig.file,
    getRemoteIssues,
    hasAdditionalForm,
    locale: numberLocale,
    onValidate,
    previewRequest,
    state,
    validateFile: resolvedConfig.validateFile,
  });
  const confirmImport = useImportConfirmation({
    canConfirm: resolvedConfig.canConfirm,
    dispatch,
    hasAdditionalForm,
    onImport,
    state,
  });

  const actions = useDataImportWizardActions({
    confirmImport,
    dispatch,
    preparePreview,
    previewRequest,
    removeFile,
    selectFile,
  });

  return {
    actions,
    /** Whether the resolved flow includes the mapping step, for a host that decides the file step's primary action. */
    hasMappingStep,
    /**
     * Exposed so a host renders from the same normalization the hook drives
     * the import with, rather than resolving the config a second time and
     * risking two divergent views of it.
     */
    resolvedConfig,
    state,
    /** The resolved flow, for a host that derives the `Stepper` index from it. */
    steps: wizardSteps,
  };
}
